import { DraggingHerdContext } from 'app/agriculture/contexts/DraggingContextProvider';
import { useState, useContext, useEffect } from 'react';
import { useHerdsOnMap } from 'app/agriculture/queries/herds/useHerdsOnMap';
import {
    isHerdPressed,
    makeDraggableMarker,
} from 'app/agriculture/hooks/useHerdDragAndDrop/dragAndDropUtils';
import { useClosestPaddockPosition } from 'app/agriculture/hooks/useHerdDragAndDrop/useClosestPaddockPosition';
import { useNavigate, useParams } from 'react-router-dom';
import { ServicesContext } from 'app/items/shared/context_interface';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { useOverlappingPaddocks } from 'app/agriculture/hooks/useHerdDragAndDrop/useOverlappingPaddocks';
import { BillboardCollection, ScreenSpaceEventHandler, ScreenSpaceEventType } from 'cesium';
import {
    disableScreenPan,
    enableScreenPan,
    getWorldPositionFromScreenPosition,
} from 'app/agriculture/hooks/useCesium/cesiumUtils';
import { useFarmPath } from 'app/agriculture/router/useFarmPath';

export function HerdDragAndDrop() {
    const { id } = useParams();
    const navigate = useNavigate();
    const farmPath = useFarmPath();
    const { mapService, agricultureToastService, mapInputHandlerService } =
        useContext(ServicesContext);
    const [collection, setCollection] = useState<BillboardCollection | null>(null);
    const { draggingHerdId, setDraggingHerdId } = useContext(DraggingHerdContext);
    const { closestPaddockPosition } = useClosestPaddockPosition();
    const { overlappingPaddocks } = useOverlappingPaddocks();
    const [selectedPaddockId, setSelectedPaddockId] = useState<string | number | undefined>(
        undefined,
    );
    const [eventHandler, setEventHandler] = useState<ScreenSpaceEventHandler>();
    const herds = useHerdsOnMap();
    const herd = herds.get(id ?? '');

    // Create the handler and destroy it when we unmount this component
    useEffect(() => {
        const handler = new ScreenSpaceEventHandler(mapService.viewer.canvas);
        setEventHandler(handler);
        return () => {
            if (!handler.isDestroyed()) {
                handler.destroy();
            }
        };
    }, [mapService.viewer.canvas]);

    // Create the billboard collection for the dragging icon
    useEffect(() => {
        const newCollection = mapService.viewer.scene.primitives.add(
            new BillboardCollection({ scene: mapService.viewer.scene }),
        ) as BillboardCollection;
        setCollection(newCollection);

        return () => {
            mapService.viewer.scene.primitives.remove(newCollection);
        };
    }, [mapService.viewer.scene]);

    // Show the dragging icon and prevent moving the map when dragging
    useEffect(() => {
        if (collection == null) return;
        if (draggingHerdId != null) {
            if (!collection.length) {
                collection.add(makeDraggableMarker(herd));
            }
            void mapInputHandlerService.setActiveHandlerByName('noopHandler');
            disableScreenPan(mapService.viewer);
        } else {
            collection.removeAll();
            enableScreenPan(mapService.viewer);
            void mapInputHandlerService.setDefaultHandler();
        }
    }, [collection, draggingHerdId, herd, mapInputHandlerService, mapService.viewer]);

    useEffect(() => {
        const mouseDown = (event: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
            if (isHerdPressed(mapService.viewer, event.position, id)) setDraggingHerdId(id);
        };
        if (eventHandler && !eventHandler.isDestroyed()) {
            eventHandler.setInputAction(mouseDown, ScreenSpaceEventType.LEFT_DOWN);
        }

        return () => {
            if (eventHandler && !eventHandler.isDestroyed()) {
                eventHandler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
            }
        };
    }, [eventHandler, id, mapService.viewer, setDraggingHerdId]);

    useEffect(() => {
        const mouseMove = (event: ScreenSpaceEventHandler.MotionEvent) => {
            if (draggingHerdId == null) return;
            const position = getWorldPositionFromScreenPosition(
                mapService.viewer,
                event.endPosition,
            );
            if (position == null) return;
            const closestPaddockData = closestPaddockPosition(position);
            const billboard = collection?.get(0);
            if (billboard) {
                billboard.position =
                    closestPaddockData.closestPaddockPole && closestPaddockData.isWithinRange
                        ? closestPaddockData.closestPaddockPole
                        : position;
            }
            const overlapping = overlappingPaddocks(position);

            if (closestPaddockData.isWithinRange) {
                setSelectedPaddockId(closestPaddockData.paddockId);
            } else if (overlapping.length > 0 && overlapping[0] != null) {
                setSelectedPaddockId(overlapping[0].id);
            }
        };
        if (eventHandler && !eventHandler.isDestroyed()) {
            eventHandler.setInputAction(mouseMove, ScreenSpaceEventType.MOUSE_MOVE);
        }

        return () => {
            if (eventHandler && !eventHandler.isDestroyed()) {
                eventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            }
        };
    }, [
        closestPaddockPosition,
        collection,
        draggingHerdId,
        eventHandler,
        id,
        mapService.viewer,
        overlappingPaddocks,
        selectedPaddockId,
        setDraggingHerdId,
    ]);

    useEffect(() => {
        const mouseUp = () => {
            if (
                selectedPaddockId != null &&
                !!herd &&
                selectedPaddockId !== herd.snapshot?.paddockId
            ) {
                // if we are hovering over a paddock, and it's not the starting paddock, navigate
                navigate(farmPath(`/herds/${id}/move`), {
                    state: { paddockId: selectedPaddockId },
                });
            } else if (draggingHerdId != null) {
                // if we are dragging and not over a paddock tell the user off
                void agricultureToastService.showToast({
                    title: 'Herd must be dragged to a new paddock',
                    subtext: '',
                    variant: 'simple',
                    type: AgricultureToastType.WARNING,
                    showOnClickAway: true,
                });
            }
            setDraggingHerdId(undefined);
        };
        if (eventHandler && !eventHandler.isDestroyed()) {
            eventHandler.setInputAction(mouseUp, ScreenSpaceEventType.LEFT_UP);
        }

        return () => {
            if (eventHandler && !eventHandler.isDestroyed()) {
                eventHandler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
            }
        };
    }, [
        agricultureToastService,
        draggingHerdId,
        eventHandler,
        farmPath,
        herd,
        id,
        navigate,
        selectedPaddockId,
        setDraggingHerdId,
    ]);

    return null;
}
